import { FlightResponse, GetFlightGroupResponse } from '@orbit/api-types'
import { formatUnix, formatWallClock } from '@lib/datetime'

export const getFlightCode = (leg: FlightResponse): string => {
	const parts: string[] = []
	if (leg.airlineIata) parts.push(leg.airlineIata)
	if (leg.flightNumber) parts.push(leg.flightNumber)
	return parts.join(' ') || 'Flight'
}

export const getRouteString = (leg: FlightResponse): string => {
	const dep = leg.departureAirportCode ?? leg.departureAirport?.iataCode ?? '—'
	const arr = leg.arrivalAirportCode ?? leg.arrivalAirport?.iataCode ?? '—'
	return `${dep} → ${arr}`
}

export const formatUnixDateTime = (unix: number | null | undefined): string | null =>
	formatUnix(unix, 'datetime')

export const formatUnixDateOnly = (unix: number | null | undefined): string | null =>
	formatUnix(unix, 'dateWeekday')

export const formatUnixTimeOnly = (unix: number | null | undefined): string | null =>
	formatUnix(unix, 'time')

export const getDepartureDisplay = (leg: FlightResponse): string | null => {
	if (leg.departureTime != null) {
		return formatUnixDateTime(leg.departureTime)
	}
	return formatWallClock(leg.departureTimeStr)
}

export const getArrivalDisplay = (leg: FlightResponse): string | null => {
	if (leg.arrivalTime != null) {
		return formatUnixDateTime(leg.arrivalTime)
	}
	return formatWallClock(leg.arrivalTimeStr)
}

export const getDurationString = (minutes: number | null | undefined): string | null => {
	if (minutes == null) return null
	const h = Math.floor(minutes / 60)
	const m = minutes % 60
	return h > 0 ? `${h}h ${m}m` : `${m}m`
}

export const getGroupEarliestDeparture = (group: GetFlightGroupResponse): number | null => {
	let earliest: number | null = null
	for (const leg of group.flights) {
		if (leg.departureTime != null) {
			if (earliest == null || leg.departureTime < earliest) {
				earliest = leg.departureTime
			}
		}
	}
	return earliest
}

export const sortGroupsByDeparture = (groups: GetFlightGroupResponse[]): GetFlightGroupResponse[] => {
	return [...groups].sort((a, b) => {
		const aTime = getGroupEarliestDeparture(a) ?? Number.MAX_SAFE_INTEGER
		const bTime = getGroupEarliestDeparture(b) ?? Number.MAX_SAFE_INTEGER
		return aTime - bTime
	})
}

/** Splits "AC123" into ("AC", "123"). */
export const splitFlightCode = (code: string): { airlineIata: string; flightNumber: string } => {
	const trimmed = code.trim().replace(/\s/g, '').toUpperCase()
	const digitIndex = trimmed.search(/\d/)
	if (digitIndex === -1) {
		return { airlineIata: trimmed, flightNumber: '' }
	}
	return {
		airlineIata: trimmed.slice(0, digitIndex),
		flightNumber: trimmed.slice(digitIndex),
	}
}

